import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { usePayment } from "../context/PaymentContext";
import "../styles/ReferralProgram.css";
import { useTranslation } from "react-i18next";
import PaymentModal from "./PaymentModal";


import { getApiUrl } from "../utils/config";
const API_URL = getApiUrl();


const ReferralProgram = () => {
    const { isAuth } = useAuth();
    const { profile, openAccountHistoryModal } = usePayment();
    const [referrals, setReferrals] = useState([]);
    const [totalEarnings, setTotalEarnings] = useState(0);
    const [monthlyEarnings, setMonthlyEarnings] = useState(0);
    const [copied, setCopied] = useState(false);
    const [error, setError] = useState("");
    const [showWithdrawModal, setShowWithdrawModal] = useState(false);
    const [alreadyWithdrawn, setAlreadyWithdrawn] = useState({});
    const [available, setAvailable] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    useEffect(() => {
        fetchReferals();
    }, [profile.selectedCurrency]);

    const fetchReferals = async () => {
        try {
            const response = await fetch(`${API_URL}/api/referrals`, {
                method: "GET",
                credentials: "include",
            });

            if (!response.ok)
                throw new Error(t("Referrals.httpError", { code: response.status }));

            const data = await response.json();

            if (data.success) {
                setReferrals(data.referrals);
                setTotalEarnings(data.totalEarnings);
                setMonthlyEarnings(data.monthlyEarnings);
                setAlreadyWithdrawn(data.alreadyWithdrawn);
                setAvailable(data.available);
                setCurrentPage(1); // Сбрасываем страницу при обновлении данных
            } else {
                throw new Error(
                    data.message || t("Referrals.loadError")   // ← перевод
                );
            }
        } catch (err) {
            setError(t("Referrals.loadError"));            // ← перевод
        }
    };

    const handleCopy = async (e) => {
        // Предотвращаем множественные клики
        if (copied) return;
        
        // Получаем значение ссылки
        const linkValue = `${window.location.origin}/?ref=${profile.MyuserId}`;
        
        try {
            // Проверяем поддержку Clipboard API
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(linkValue);
            } else {
                // Fallback для старых браузеров или небезопасного контекста
                const textArea = document.createElement('textarea');
                textArea.value = linkValue;
                textArea.style.position = 'fixed';
                textArea.style.left = '-999999px';
                textArea.style.top = '-999999px';
                document.body.appendChild(textArea);
                textArea.focus();
                textArea.select();
                
                const successful = document.execCommand('copy');
                document.body.removeChild(textArea);
                
                if (!successful) {
                    throw new Error('execCommand failed');
                }
            }
            
            setCopied(true);
            setTimeout(() => {
                setCopied(false);
            }, 2000);
            
        } catch (error) {
            console.error('Ошибка при копировании:', error);
            
            // Показываем пользователю ошибку
            setError('Не удалось скопировать ссылку. Попробуйте выделить и скопировать вручную.');
            setTimeout(() => setError(''), 3000);
        }
    };

    // Функция для получения minAmount (эквивалент 15000 RUB в выбранной валюте)
    const getMinAmount = () => {
        const rates = profile.fiatRates || {};
        const cur = profile.selectedCurrency || 'RUB';
        if (cur === 'RUB') return 15000;
        if (cur === 'USD' && rates.RUB_USD) return Math.ceil(15000 * rates.RUB_USD);
        if (cur === 'BRL' && rates.RUB_BRL) return Math.ceil(15000 * rates.RUB_BRL);
        return 15000;
    };
    // Максимум — сколько доступно к выводу по всем валютам (в выбранной валюте)
    const getMaxAmount = () => {
        const cur = profile.selectedCurrency || 'RUB';
        if (!available) return 0;
        if (typeof available === 'object') return available[cur] || 0;
        return available;
    };

    // Функция для пересчёта доступного к выводу в долларах
    const getAvailableTotalUSD = () => {
        if (!available || typeof available !== 'object') return 0;
        const rates = profile.fiatRates || {};
        let sum = 0;
        for (const [cur, val] of Object.entries(available)) {
            if (cur === 'USD') sum += Number(val);
            else if (cur === 'RUB' && rates.RUB_USD) sum += Number(val) * rates.RUB_USD;
            else if (cur === 'BRL' && rates.BRL_USD) sum += Number(val) * rates.BRL_USD;
        }
        return sum;
    };
    const availableTotalUSD = getAvailableTotalUSD();

    const { t } = useTranslation();


    if (!isAuth) {
        return <div className="profile-container">{t("Referrals.notAuth")}</div>;
    }

    return (
        <div className="referral-container">

            <h2 className="referral-title">{t("Referrals.title")}</h2>
            <div className="referral-description">
                {t("Referrals.descStart")} <b style={{ color: '#28bc10' }}>{profile.refPercent ?? 0}%</b> {t("Referrals.descEnd")}
            </div>





            <div className="referral-block">
                <div className="referral-link-label">{t("Referrals.linkLabel")}</div>
                <div className="referral-copy-wrapper">
                    <input
                        type="text"
                        className="referral-copy-input"
                        readOnly
                        value={`${window.location.origin}/?ref=${profile.MyuserId}`}
                        onClick={(e) => e.target.select()}
                        title="Нажмите для выделения ссылки"
                    />
                    <button
                        className="referral-copy-btn"
                        onClick={handleCopy}
                        disabled={copied}
                    >
                        {copied ? t("Referrals.copied") : t("Referrals.copy")}
                    </button>
                </div>
                <div className="referral-link-hint">{t("Referrals.linkHint")}</div>
            </div>

            {/* Ваш процент с рефералов */}
            <div className="referral-refpercent-block" style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 32, background: 'linear-gradient(135deg,#28bc10 60%,#4891ff 100%)', borderRadius: 14, padding: '18px 28px', color: '#fff', fontWeight: 700, fontSize: 20, boxShadow: '0 2px 12px 0 #28bc1044' }}>
                <span style={{ fontSize: 28, fontWeight: 900, background: '#fff', color: '#28bc10', borderRadius: '50%', width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: 10 }}> % </span>
                Ваш процент с рефералов: <span style={{ fontSize: 26, fontWeight: 900, marginLeft: 8 }}>{profile.refPercent ?? 0}%</span>
            </div>

            <div className="referral-how">
                <b>{t("Referrals.howTitle")}</b><br />
                {t("Referrals.howStep1")}<br />
                {t("Referrals.howStep2")}<br />
                {t("Referrals.howStep3")}<br />
                {t("Referrals.howStep4")}<br />
                {t("Referrals.howStep5")}
            </div>

            {/* Кнопка истории счёта и подсказка про статус заявок */}
            <div style={{ margin: '20px 0', display: 'flex', alignItems: 'center', gap: 12 }}>
                <button onClick={openAccountHistoryModal} style={{ padding: '10px 16px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg,#571CA5 0%, #8443b9 100%)', color: '#fff', fontWeight: 700, cursor: 'pointer' }}>
                    История счёта
                </button>
                <span style={{ color: '#b6b6c6', fontSize: 14 }}>Здесь можно посмотреть статус заявки на вывод</span>
            </div>

            {/* Новый красивый блок по валютам (динамический) */}
            <div className="referral-currency-actions-modern">
                {Array.from(new Set([
                    ...Object.keys(totalEarnings || {}),
                    ...Object.keys(alreadyWithdrawn || {}),
                    ...Object.keys(available || {}),
                    'USD', 'RUB', 'BRL'
                ])).filter(Boolean).map(cur => {
                    const withdrawn = alreadyWithdrawn && alreadyWithdrawn[cur] ? Number(alreadyWithdrawn[cur]) : 0;
                    const avail = available && available[cur] ? Number(available[cur]) : 0;
                    const earned = totalEarnings && totalEarnings[cur] ? Number(totalEarnings[cur]) : 0;
                    let symbol = cur;
                    let icon = null;
                    if (cur === 'RUB') { symbol = '₽'; icon = <span className="currency-icon rub">₽</span>; }
                    else if (cur === 'USD') { symbol = '$'; icon = <span className="currency-icon usd">$</span>; }
                    else if (cur === 'BRL') { symbol = 'R$'; icon = <span className="currency-icon brl">R$</span>; }
                    else if (cur === 'USDT') { symbol = '₮'; icon = <span className="currency-icon usdt">₮</span>; }
                    else if (cur === 'EUR') { symbol = '€'; icon = <span className="currency-icon eur">€</span>; }
                    const canWithdraw = avail > 0;
                    return (
                        <div key={cur} className="ref-currency-row-modern" style={{ display: 'flex', alignItems: 'center', gap: 24, marginBottom: 18, background: 'linear-gradient(120deg,#23232e 80%,#2e2e44 100%)', borderRadius: 18, padding: 22, boxShadow: '0 4px 24px 0 #9735e233' }}>
                            {icon}
                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                                <div style={{ fontWeight: 700, fontSize: 18, color: '#fff', marginBottom: 2 }}>{symbol} — {cur}</div>
                                <div style={{ fontSize: 15, color: '#b6b6c6' }}>Заработано: <b style={{ color: '#28bc10' }}>{earned.toFixed(2)} {symbol}</b></div>
                                <div style={{ fontSize: 15, color: '#b6b6c6' }}>Уже выведено: <b style={{ color: '#ffb300' }}>{withdrawn.toFixed(2)} {symbol}</b></div>
                                <div style={{ fontSize: 15, color: '#b6b6c6' }}>Доступно к выводу: <b style={{ color: canWithdraw ? '#28bc10' : '#b6b6c6', fontSize: 18 }}>{avail.toFixed(2)} {symbol}</b></div>
                            </div>
                            <button
                                className="ref-currency-withdraw-btn-modern"
                                disabled={!canWithdraw}
                                onClick={() => setShowWithdrawModal(cur)}
                                style={{ padding: '12px 32px', borderRadius: 12, fontWeight: 700, fontSize: 17, background: canWithdraw ? 'linear-gradient(91.32deg, #28bc10 0%, #4891ff 100%)' : '#eaeaea', color: canWithdraw ? '#fff' : '#b6b6c6', border: 'none', cursor: canWithdraw ? 'pointer' : 'not-allowed', boxShadow: canWithdraw ? '0 4px 16px 0 #28bc1088' : 'none', transition: 'all 0.18s' }}
                            >
                                {canWithdraw ? 'Вывести' : 'Недоступно'}
                            </button>
                        </div>
                    );
                })}
            </div>

            {/* Таблица рефералов с пагинацией */}
            <div className="referral-table-block" style={{ marginTop: 40 }}>
                <div className="referral-table-title">{t("Referrals.tableTitle")}</div>
                
                {(() => {
                    // Подсчитываем количество строк для пагинации (с учетом валют)
                    const allRows = referrals && referrals.length > 0 ? referrals.flatMap(ref => {
                        const currencies = Object.keys(ref.stats);
                        return currencies.length === 0 ? [ref] : currencies.map((cur, idx) => ({ ref, cur, idx }));
                    }) : [];
                    
                    const totalRows = allRows.length;
                    const totalPages = Math.ceil(totalRows / itemsPerPage);
                    const startIndex = (currentPage - 1) * itemsPerPage;
                    const endIndex = startIndex + itemsPerPage;
                    const paginatedRows = allRows.slice(startIndex, endIndex);
                    
                    return (
                        <>
                            {referrals && referrals.length > 0 ? (
                                <>
                                    {/* Десктопная версия таблицы */}
                                    <div className="referral-table-wrapper">
                                        <table className="referral-table">
                                            <thead>
                                                <tr>
                                                    <th>{t("Referrals.emailHeader")}</th>
                                                    <th>{t("Referrals.currencyHeader")}</th>
                                                    <th>{t("Referrals.betsHeader")}</th>
                                                    <th>{t("Referrals.lossesHeader")}</th>
                                                    <th>{t("Referrals.casinoProfitHeader")}</th>
                                                    <th>{t("Referrals.refererPercentHeader")}</th>
                                                    <th>{t("Referrals.earnedHeader")}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {paginatedRows.map((row, rowIdx) => {
                                                    if (!row.cur) {
                                                        // Реферал без валют
                                                        return (
                                                            <tr key={row.id}>
                                                                <td>{row.email}</td>
                                                                <td>-</td>
                                                                <td>0.00</td>
                                                                <td>0.00</td>
                                                                <td>0.00</td>
                                                                <td>{Number(row.referrer_percent) ?? 0}%</td>
                                                                <td className="earnings">0.00</td>
                                                            </tr>
                                                        );
                                                    }
                                                    const { ref, cur, idx } = row;
                                                    return (
                                                        <tr key={ref.id + '-' + cur}>
                                                            <td>{idx === 0 ? ref.email : ''}</td>
                                                            <td>{cur}</td>
                                                            <td>{Number(ref.stats[cur].total_bets).toFixed(2)}</td>
                                                            <td>{Number(ref.stats[cur].total_losses).toFixed(2)}</td>
                                                            <td>{Number(ref.stats[cur].casino_profit).toFixed(2)}</td>
                                                            <td>{Number(ref.referrer_percent) ?? 0}%</td>
                                                            <td className="earnings">{Number(ref.stats[cur].earnings).toFixed(2)}</td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Мобильная версия таблицы */}
                                    <div className="referral-table-mobile">
                                        {paginatedRows.map((row, rowIdx) => {
                                            if (!row.cur) {
                                                // Реферал без валют
                                                return (
                                                    <div key={row.id} className="mobile-row">
                                                        <div className="mobile-row-header">
                                                            <div className="mobile-row-title">{row.email}</div>
                                                            <div className="mobile-row-currency">-</div>
                                                        </div>
                                                        <div className="mobile-row-data">
                                                            <div className="mobile-data-item">
                                                                <div className="mobile-data-label">{t("Referrals.betsHeader")}</div>
                                                                <div className="mobile-data-value">0.00</div>
                                                            </div>
                                                            <div className="mobile-data-item">
                                                                <div className="mobile-data-label">{t("Referrals.lossesHeader")}</div>
                                                                <div className="mobile-data-value">0.00</div>
                                                            </div>
                                                            <div className="mobile-data-item">
                                                                <div className="mobile-data-label">{t("Referrals.casinoProfitHeader")}</div>
                                                                <div className="mobile-data-value">0.00</div>
                                                            </div>
                                                            <div className="mobile-data-item">
                                                                <div className="mobile-data-label">{t("Referrals.refererPercentHeader")}</div>
                                                                <div className="mobile-data-value percent">{Number(row.referrer_percent) ?? 0}%</div>
                                                            </div>
                                                            <div className="mobile-data-item full-width">
                                                                <div className="mobile-data-label">{t("Referrals.earnedHeader")}</div>
                                                                <div className="mobile-data-value earnings">0.00</div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            }
                                            const { ref, cur, idx } = row;
                                            return (
                                                <div key={ref.id + '-' + cur} className="mobile-row">
                                                    <div className="mobile-row-header">
                                                        <div className="mobile-row-title">
                                                            {idx === 0 ? ref.email : ''}
                                                        </div>
                                                        <div className="mobile-row-currency">{cur}</div>
                                                    </div>
                                                    <div className="mobile-row-data">
                                                        <div className="mobile-data-item">
                                                            <div className="mobile-data-label">{t("Referrals.betsHeader")}</div>
                                                            <div className="mobile-data-value">{Number(ref.stats[cur].total_bets).toFixed(2)}</div>
                                                        </div>
                                                        <div className="mobile-data-item">
                                                            <div className="mobile-data-label">{t("Referrals.lossesHeader")}</div>
                                                            <div className="mobile-data-value">{Number(ref.stats[cur].total_losses).toFixed(2)}</div>
                                                        </div>
                                                        <div className="mobile-data-item">
                                                            <div className="mobile-data-label">{t("Referrals.casinoProfitHeader")}</div>
                                                            <div className="mobile-data-value">{Number(ref.stats[cur].casino_profit).toFixed(2)}</div>
                                                        </div>
                                                        <div className="mobile-data-item">
                                                            <div className="mobile-data-label">{t("Referrals.refererPercentHeader")}</div>
                                                            <div className="mobile-data-value percent">{Number(ref.referrer_percent) ?? 0}%</div>
                                                        </div>
                                                        <div className="mobile-data-item full-width">
                                                            <div className="mobile-data-label">{t("Referrals.earnedHeader")}</div>
                                                            <div className="mobile-data-value earnings">{Number(ref.stats[cur].earnings).toFixed(2)}</div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>

                                    {/* Пагинация */}
                                    {totalPages > 1 && (
                                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 10, marginTop: 20, flexWrap: 'wrap' }}>
                                            <button
                                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                                disabled={currentPage === 1}
                                                style={{
                                                    padding: '8px 16px',
                                                    borderRadius: 8,
                                                    border: 'none',
                                                    background: currentPage === 1 ? '#eaeaea' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                    color: currentPage === 1 ? '#999' : '#fff',
                                                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                                                    fontWeight: 600,
                                                    fontSize: 14
                                                }}
                                            >
                                                ← Предыдущая
                                            </button>
                                            <span style={{ color: '#fff', fontWeight: 600 }}>
                                                Страница {currentPage} из {totalPages}
                                            </span>
                                            <button
                                                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                                disabled={currentPage === totalPages}
                                                style={{
                                                    padding: '8px 16px',
                                                    borderRadius: 8,
                                                    border: 'none',
                                                    background: currentPage === totalPages ? '#eaeaea' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                    color: currentPage === totalPages ? '#999' : '#fff',
                                                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                                                    fontWeight: 600,
                                                    fontSize: 14
                                                }}
                                            >
                                                Следующая →
                                            </button>
                                        </div>
                                    )}
                                </>
                            ) : referrals === undefined || referrals === null ? (
                                <div className="referral-no-data-block" style={{ backgroundColor: 'rgba(255, 255, 0, 0.1)', border: '2px solid yellow', padding: '20px' }}>
                                    <div className="referral-no-data-icon">⏳</div>
                                    <div className="referral-no-data-text">Загрузка данных...</div>
                                </div>
                            ) : (
                                <div className="referral-no-data-block" style={{ backgroundColor: 'rgba(255, 0, 0, 0.1)', border: '2px solid red', padding: '20px' }}>
                                    <div className="referral-no-data-icon">👥</div>
                                    <div className="referral-no-data-text">{t("Referrals.noData")}</div>
                                    <div className="referral-no-data-hint">Делитесь реферальной ссылкой с другом и начинайте зарабатывать!</div>
                                </div>
                            )}
                        </>
                    );
                })()}
            </div>

            {showWithdrawModal && (
                <PaymentModal
                    transactionType="withdraw"
                    selectedCurrency={typeof showWithdrawModal === 'string' ? showWithdrawModal : (profile.selectedCurrency || 'RUB')}
                    onClose={() => setShowWithdrawModal(false)}
                    withdrawType="referal"
                    minAmount={0}
                    maxAmount={available && typeof showWithdrawModal === 'string' ? (available[showWithdrawModal] || 0) : 0}
                    defaultAmount={available && typeof showWithdrawModal === 'string' ? (available[showWithdrawModal] || 0) : 0}
                    hideBalanceBlock={true}
                />
            )}

            

            {error && <p style={{ color: "#ff4b4b", fontWeight: 600 }}>{error}</p>}
        </div>
    );
};

export default ReferralProgram; 